import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@clerk/clerk-react';
import { Surface } from './ui/Surface';
import type { Player } from '@/types/player';
import { formatNotesWait } from '@/utils/notesQuota';
import { compactNoteItems, outlookItems, parseRosterNotes, type RosterNotes as Notes } from '@/utils/rosterNotes';

interface Props {
  roster: Player[];
  scoring: string;
}

export function RosterNotes({ roster, scoring }: Props) {
  const { getToken } = useAuth();
  const [notes, setNotes] = useState<Notes | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [week, setWeek] = useState<number | null>(null);
  const [retryAt, setRetryAt] = useState<string | null>(null);

  const locked = Boolean(retryAt && Date.parse(retryAt) > Date.now());
  const rosterNames = useMemo(
    () => roster.map((player) => player.name).filter(Boolean),
    [roster]
  );

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [getToken]);

  const load = useCallback(async (force = false) => {
    if (roster.length < 3) {
      setNotes(null);
      setError(null);
      return;
    }

    const cacheKey = cacheId(roster);
    if (!force) {
      const cached = readCache(cacheKey);
      if (cached) {
        setNotes(cached.notes);
        setWeek(cached.week);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/roster/weekly-breakdown', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          week: 1,
          scoring,
          league: { scoring },
          roster: roster.slice(0, 16).map((player) => ({
            id: player.id,
            name: player.name,
            position: player.position,
            team: player.team,
          })),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (data.retryAt) setRetryAt(data.retryAt);
      if (!response.ok) {
        throw new Error(data.error || 'Could not generate notes.');
      }
      const parsed = data.notes && (data.notes.outlook || data.notes.startSit?.length)
        ? data.notes as Notes
        : parseRosterNotes(data.raw || '');
      setNotes(parsed);
      setWeek(data.week ?? 1);
      writeCache(cacheKey, parsed, data.week ?? 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate notes.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, roster, scoring]);

  useEffect(() => {
    if (roster.length < 3) {
      setNotes(null);
      setError(null);
      return;
    }
    const cached = readCache(cacheId(roster));
    if (cached) {
      setNotes(cached.notes);
      setWeek(cached.week);
    }
    void (async () => {
      try {
        const response = await fetch('/api/roster/weekly-breakdown', {
          headers: await authHeaders(),
        });
        const data = await response.json().catch(() => ({}));
        if (data.retryAt) setRetryAt(data.retryAt);
      } catch {
        // quota check is best-effort
      }
    })();
  }, [authHeaders, roster]);

  if (roster.length === 0) return null;

  return (
    <Surface className="border-white/15 bg-black/50 p-4 shadow-xl shadow-black/20 backdrop-blur-xl sm:p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-white">
            {week ? `Week ${week} notes` : 'Roster notes'}
          </h2>
          <p className="mt-1 text-sm text-zinc-300">
            {locked ? formatNotesWait(retryAt) : 'One set of notes per account each day.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load(true)}
          disabled={loading || roster.length < 3 || locked}
          className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-white/20 hover:text-white disabled:opacity-50"
        >
          {loading ? 'Reading' : locked ? 'Locked' : notes ? 'Refresh' : 'Get notes'}
        </button>
      </div>

      {roster.length < 3 ? (
        <p className="text-sm text-zinc-300">Add at least three players for notes.</p>
      ) : null}

      {!loading && !notes && !error && roster.length >= 3 ? (
        <p className="text-sm text-zinc-300">
          {locked ? formatNotesWait(retryAt) : 'Get notes when you want a live read on this roster.'}
        </p>
      ) : null}

      {loading ? <NotesWire roster={roster} /> : null}

      {error && !loading ? (
        <p className="text-sm text-rose-200">{error}</p>
      ) : null}

      {notes && !loading ? (
        <div className="space-y-6">
          <NoteSection title="Start and sit" items={compactNoteItems(notes.startSit, 4, rosterNames)} showCall tone="yellow" />
          <NoteSection title="Player news" items={compactNoteItems(notes.news, 4, rosterNames)} tone="cyan" />
          <NoteSection
            title="Trades"
            items={compactNoteItems(notes.trades, 3, rosterNames)}
            tone="pink"
            extra={(
              <Link to="/trade-calculator" className="text-xs font-medium text-zinc-300 hover:text-fiesta-yellow">
                Trade desk
              </Link>
            )}
          />
          <NoteSection title="Rest-of-week outlook" items={outlookItems(notes.outlook)} tone="neutral" />
        </div>
      ) : null}
    </Surface>
  );
}

function NoteSection({
  title,
  items,
  extra,
  showCall = false,
  tone = 'neutral',
}: {
  title: string;
  items: ReturnType<typeof compactNoteItems>;
  extra?: React.ReactNode;
  showCall?: boolean;
  tone?: 'yellow' | 'cyan' | 'pink' | 'neutral';
}) {
  const toneClass = {
    yellow: 'text-fiesta-yellow',
    cyan: 'text-fiesta-blue',
    pink: 'text-fiesta-pink',
    neutral: 'text-zinc-300',
  }[tone];
  const railClass = {
    yellow: 'border-l-fiesta-yellow/70',
    cyan: 'border-l-fiesta-blue/70',
    pink: 'border-l-fiesta-pink/70',
    neutral: 'border-l-white/25',
  }[tone];

  return (
    <section className="border-t border-white/10 pt-5 first:border-t-0 first:pt-0">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className={`text-xs font-semibold uppercase tracking-[0.16em] ${toneClass}`}>{title}</h3>
        {extra}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-400">Nothing standing out.</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((note) => (
            <li key={`${note.name}-${note.body}`} className={`border-l-2 ${railClass} bg-white/[0.025] px-3 py-2.5`}>
              {note.name ? (
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="font-display text-[15px] font-semibold tracking-tight text-white">
                    {note.name}
                  </h4>
                  {showCall && note.call ? (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      note.call === 'Start'
                        ? 'bg-fiesta-yellow/15 text-fiesta-yellow'
                        : 'bg-zinc-400/10 text-zinc-200'
                    }`}
                    >
                      {note.call}
                    </span>
                  ) : null}
                </div>
              ) : showCall && note.call ? (
                <p className={`text-xs font-semibold ${
                  note.call === 'Start' ? 'text-fiesta-yellow' : 'text-zinc-200'
                }`}
                >
                  {note.call}
                </p>
              ) : null}
              <p className={`${note.name ? 'mt-1.5' : ''} text-sm leading-relaxed text-zinc-200`}>{note.body}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function NotesWire({ roster }: { roster: Player[] }) {
  const names = useMemo(
    () => roster.slice(0, 8).map((player) => player.name).filter(Boolean),
    [roster]
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (names.length === 0) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % names.length);
    }, 900);
    return () => window.clearInterval(timer);
  }, [names.length]);

  return (
    <div className="notes-wire rounded-xl border border-white/10 bg-white/[0.02] px-4 py-5" aria-busy="true">
      <div className="notes-wire-scan" />
      <div className="relative">
        <div className="mb-3 flex items-center gap-2">
          <span className="notes-wire-dot h-1.5 w-1.5 rounded-full bg-fiesta-yellow" />
          <p className="text-xs text-fiesta-yellow">Reading the wire</p>
        </div>
        <AnimatePresence mode="wait">
          <motion.p
            key={names[index] || 'desk'}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22 }}
            className="text-base font-medium text-white"
          >
            {names[index] || 'This roster'}
          </motion.p>
        </AnimatePresence>
        <p className="mt-1 text-xs text-zinc-500">
          Injuries, depth charts, and matchups
        </p>
      </div>
    </div>
  );
}

function cacheId(roster: Player[]): string {
  const today = new Date().toISOString().slice(0, 10);
  return `roster-notes-${today}-${roster.map((player) => String(player.id)).sort().join('-')}`;
}

function readCache(key: string): { notes: Notes; week: number } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as { notes: Notes; week: number };
  } catch {
    return null;
  }
}

function writeCache(key: string, notes: Notes, week: number) {
  try {
    localStorage.setItem(key, JSON.stringify({ notes, week }));
  } catch {
    // ignore quota
  }
}
